/**
 * Local Bridge MarketFit: create a new deal with multiple products and situations.
 * Uses local bridge MarketFit API (products, situations, deals).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, Button, Divider, message, Spin } from 'antd';
import { AimOutlined, PlusOutlined, SolutionOutlined, BulbOutlined } from '@ant-design/icons';
import {
  listMarketFitProducts,
  createMarketFitProduct,
  listMarketFitSituations,
  createMarketFitSituation,
  createMarketFitDeal,
  type MarketFitProduct,
  type MarketFitSituation,
} from '../../services/localBridgeSessionApi';

const { TextArea } = Input;

interface LocalBridgeNewDealDialogProps {
  open: boolean;
  companyId: string;
  bridgeSessionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const LocalBridgeNewDealDialog: React.FC<LocalBridgeNewDealDialogProps> = ({
  open,
  companyId,
  bridgeSessionId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<{ name: string; description?: string; product_ids: string[]; situation_ids: string[] }>();
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [products, setProducts] = useState<MarketFitProduct[]>([]);
  const [situations, setSituations] = useState<MarketFitSituation[]>([]);
  const [createProductName, setCreateProductName] = useState('');
  const [createSituationName, setCreateSituationName] = useState('');
  const [addingProduct, setAddingProduct] = useState(false);
  const [addingSituation, setAddingSituation] = useState(false);

  const loadOptions = useCallback(async () => {
    if (!open || !companyId) return;
    setLoadingOptions(true);
    try {
      const [pList, sList] = await Promise.all([
        listMarketFitProducts(companyId),
        listMarketFitSituations(companyId),
      ]);
      setProducts(pList);
      setSituations(sList);
    } catch {
      setProducts([]);
      setSituations([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [open, companyId]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const handleAddProduct = async () => {
    const name = createProductName?.trim();
    if (!name) {
      message.warning('Enter a product name');
      return;
    }
    setAddingProduct(true);
    try {
      const created = await createMarketFitProduct({ company_id: companyId, name });
      if (created) {
        setProducts((prev) => [created, ...prev]);
        form.setFieldValue('product_ids', [...(form.getFieldValue('product_ids') || []), created.id]);
        setCreateProductName('');
        message.success(`Product "${name}" created`);
      } else {
        message.error('Failed to create product');
      }
    } finally {
      setAddingProduct(false);
    }
  };

  const handleAddSituation = async () => {
    const name = createSituationName?.trim();
    if (!name) {
      message.warning('Enter a situation name');
      return;
    }
    setAddingSituation(true);
    try {
      const created = await createMarketFitSituation({ company_id: companyId, name });
      if (created) {
        setSituations((prev) => [created, ...prev]);
        form.setFieldValue('situation_ids', [...(form.getFieldValue('situation_ids') || []), created.id]);
        setCreateSituationName('');
        message.success(`Situation "${name}" created`);
      } else {
        message.error('Failed to create situation');
      }
    } finally {
      setAddingSituation(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const productIds = values.product_ids ?? [];
      const situationIds = values.situation_ids ?? [];
      if (productIds.length === 0) {
        message.warning('Select or create at least one product');
        return;
      }
      if (situationIds.length === 0) {
        message.warning('Select or create at least one situation');
        return;
      }
      setLoading(true);
      const deal = await createMarketFitDeal({
        company_id: companyId,
        bridge_session_id: bridgeSessionId,
        name: values.name,
        description: values.description?.trim() || undefined,
        product_ids: productIds,
        situation_ids: situationIds,
      });
      if (deal) {
        form.resetFields();
        onSuccess();
      } else {
        message.error('Failed to create deal');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please fill required fields');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCreateProductName('');
    setCreateSituationName('');
    onClose();
  };

  const productOptions = products.map((p) => ({ value: p.id, label: p.name }));
  const situationOptions = situations.map((s) => ({ value: s.id, label: s.name || s.situation_name || s.id }));

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AimOutlined /> New Deal
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Create Deal"
      width={560}
      destroyOnClose
    >
      <Spin spinning={loadingOptions}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ product_ids: [], situation_ids: [] }}
        >
          <Form.Item
            name="name"
            label="Deal Name"
            rules={[
              { required: true, message: 'Enter a deal name' },
              { min: 2, message: 'At least 2 characters' },
            ]}
          >
            <Input placeholder="e.g. UBER driver–passenger deal" maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea placeholder="Optional" rows={2} maxLength={500} />
          </Form.Item>

          <Divider orientation="left" plain>
            <SolutionOutlined /> Products
          </Divider>
          <Form.Item
            name="product_ids"
            label="Select products (one or more)"
            rules={[{ required: true, type: 'array', min: 1, message: 'Select or create at least one product' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select products"
              options={productOptions}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '8px', display: 'flex', gap: 8 }}>
                    <Input
                      placeholder="New product name"
                      value={createProductName}
                      onChange={(e) => setCreateProductName(e.target.value)}
                      onPressEnter={(e) => {
                        e.preventDefault();
                        void handleAddProduct();
                      }}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      loading={addingProduct}
                      onClick={handleAddProduct}
                    >
                      Create
                    </Button>
                  </div>
                </>
              )}
            />
          </Form.Item>

          <Divider orientation="left" plain>
            <BulbOutlined /> Situations
          </Divider>
          <Form.Item
            name="situation_ids"
            label="Select situations (one or more)"
            rules={[{ required: true, type: 'array', min: 1, message: 'Select or create at least one situation' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select situations"
              options={situationOptions}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '8px', display: 'flex', gap: 8 }}>
                    <Input
                      placeholder="New situation name"
                      value={createSituationName}
                      onChange={(e) => setCreateSituationName(e.target.value)}
                      onPressEnter={(e) => {
                        e.preventDefault();
                        void handleAddSituation();
                      }}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      loading={addingSituation}
                      onClick={handleAddSituation}
                    >
                      Create
                    </Button>
                  </div>
                </>
              )}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default LocalBridgeNewDealDialog;
